import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const HeroSection = styled.section`
  position: relative;
  height: 100vh;
  background: ${props => props.theme.colors.gradients.hero};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('/assets/images/hero-video-fallback.jpg') center/cover;
    z-index: 1;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${props => props.theme.colors.gradients.hero};
    z-index: 3;
    opacity: 0.3;
  }
`;

const VideoBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 2;
  overflow: hidden;
  
  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: absolute;
    top: 0;
    left: 0;
    min-width: 100%;
    min-height: 100%;
    z-index: 1;
    display: block !important;
    visibility: visible !important;
    opacity: 1 !important;
  }
  
  .video-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.1);
    z-index: 2;
  }
  
  .video-fallback {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: url('/assets/images/hero-video-fallback.jpg') center/cover;
    z-index: 0;
  }
`;

const HeroContent = styled.div`
  text-align: center;
  max-width: 800px;
  padding: 0 2rem;
  z-index: 4;
`;

const HeroTitle = styled(motion.h1)`
  font-family: ${props => props.theme.fonts.script};
  font-size: clamp(3rem, 8vw, 6rem);
  font-weight: 700;
  margin-bottom: 1rem;
  text-shadow: 3px 3px 8px rgba(0, 0, 0, 0.4);
  line-height: 1.2;
  
  .title-main {
    display: block;
    margin-bottom: 0.5rem;
  }
  
  .title-subtitle {
    display: block;
    font-size: 0.6em;
    font-weight: 400;
    opacity: 0.9;
  }
`;

const HeroDescription = styled(motion.p)`
  font-size: clamp(1.1rem, 2.5vw, 1.4rem);
  line-height: 1.6;
  margin-bottom: 2rem;
  opacity: 0.95;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
`;

const HeroButtons = styled(motion.div)`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 2rem;
`;

const HeroButton = styled(Link)`
  display: inline-block;
  padding: 1rem 2rem;
  border-radius: 25px;
  font-weight: 600;
  font-size: 1.1rem;
  text-decoration: none;
  transition: all 0.3s ease;
  min-width: 160px;
  text-align: center;
  
  &.btn-primary {
    background: linear-gradient(45deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary});
    color: white;
    box-shadow: 0 5px 15px rgba(46, 204, 113, 0.4);
    
    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 8px 25px rgba(46, 204, 113, 0.6);
    }
  }
  
  &.btn-secondary {
    background: ${props => props.theme.colors.gradients.secondary};
    color: white;
    box-shadow: 0 5px 15px rgba(46, 204, 113, 0.35);
    
    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 8px 25px rgba(46, 204, 113, 0.55);
    }
  }
  
  @media (max-width: 480px) {
    width: 100%;
    margin-bottom: 0.5rem;
  }
`;

const ScrollIndicator = styled(motion.div)`
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  text-align: center;
  color: white;
  cursor: pointer;
  
  .scroll-mouse {
    width: 24px;
    height: 40px;
    border: 2px solid rgba(255, 255, 255, 0.8);
    border-radius: 12px;
    margin: 0 auto 0.5rem;
    position: relative;
    
    .scroll-wheel {
      width: 4px;
      height: 8px;
      background: rgba(255, 255, 255, 0.8);
      border-radius: 2px;
      position: absolute;
      top: 8px;
      left: 50%;
      transform: translateX(-50%);
      animation: scroll 2s infinite;
    }
  }
  
  span {
    font-size: 0.9rem;
    opacity: 0.8;
  }
  
  @keyframes scroll {
    0% { opacity: 1; transform: translateX(-50%) translateY(0); }
    50% { opacity: 0.5; transform: translateX(-50%) translateY(8px); }
    100% { opacity: 1; transform: translateX(-50%) translateY(0); }
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const Hero = () => {
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [currentFormat, setCurrentFormat] = useState('mov');
  const [videoError, setVideoError] = useState(null);
  const [showVideo, setShowVideo] = useState(true);
  const [videoAttempts, setVideoAttempts] = useState(0);

  useEffect(() => {
    // Versuche das Video zu laden, auch wenn der Browser es nicht perfekt unterstützt
    console.log('Versuche Video zu laden...');
    
    // Setze einen Timeout, um sicherzustellen, dass das Video geladen wird
    const videoTimeout = setTimeout(() => {
      if (!videoLoaded && videoAttempts === 0) {
        console.log('Video-Loading-Timeout, versuche alternatives Format...');
        setCurrentFormat('mp4');
        setVideoAttempts(1);
      }
    }, 3000);

    return () => clearTimeout(videoTimeout);
  }, [videoLoaded, videoAttempts]);

  const handleVideoLoad = () => {
    setVideoLoaded(true);
    setVideoError(null);
    console.log('Video erfolgreich geladen');
  };

  const handleVideoError = (e) => {
    console.error('Video-Fehler:', e);
    setVideoAttempts(prev => prev + 1);
    
    // Versuche das nächste Format
    const formats = ['mov', 'mp4', 'webm'];
    const currentIndex = formats.indexOf(currentFormat);
    const nextFormat = formats[(currentIndex + 1) % formats.length];
    
    if (nextFormat !== currentFormat && videoAttempts < 3) {
      console.log(`Versuche nächstes Format: ${nextFormat} (Versuch ${videoAttempts + 1})`);
      setCurrentFormat(nextFormat);
      setVideoLoaded(false);
    } else {
      // Alle Formate versucht, aber lass das Video trotzdem angezeigt werden
      console.warn('Alle Video-Formate fehlgeschlagen, aber Video wird trotzdem angezeigt');
      setVideoError('Video konnte nicht geladen werden, aber wird trotzdem angezeigt');
      // Zeige trotzdem das Video an, der Browser wird es versuchen abzuspielen
    }
  };

  const scrollToNext = () => {
    window.scrollTo({
      top: window.innerHeight,
      behavior: 'smooth'
    });
  };

  const getVideoSource = () => {
    switch (currentFormat) {
      case 'mp4':
        return '/assets/videos/start.mp4';
      case 'webm':
        return '/assets/videos/start.webm';
      case 'mov':
      default:
        return '/assets/videos/start.mov';
    }
  };

  // Debug-Informationen in der Entwicklung
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('Video-Status:', {
        loaded: videoLoaded,
        format: currentFormat,
        error: videoError,
        showVideo: showVideo,
        attempts: videoAttempts
      });
    }
  }, [videoLoaded, currentFormat, videoError, showVideo, videoAttempts]);

  return (
    <HeroSection>
      <VideoBackground>
        {/* Fallback-Bild im Hintergrund */}
        <div className="video-fallback"></div>
        
        {/* Video-Element */}
        <video 
          key={`${currentFormat}-${videoAttempts}`}
          autoPlay 
          muted 
          loop 
          playsInline
          preload="auto"
          onLoadedData={handleVideoLoad}
          onError={handleVideoError}
          onLoadStart={() => console.log('Video wird geladen...')}
          onCanPlay={() => console.log('Video kann abgespielt werden')}
          style={{ 
            opacity: videoLoaded ? 1 : 0.8, 
            transition: 'opacity 1s ease',
            filter: videoLoaded ? 'none' : 'brightness(0.8)',
            display: 'block',
            visibility: 'visible'
          }}
        >
          <source src={getVideoSource()} type={`video/${currentFormat === 'mov' ? 'mp4' : currentFormat}`} />
          <source src="/assets/videos/start.mp4" type="video/mp4" />
          <source src="/assets/videos/start.webm" type="video/webm" />
          Your browser does not support the video tag.
        </video>
        
        {/* Video-Overlay für bessere Lesbarkeit */}
        <div className="video-overlay"></div>
      </VideoBackground>
      
      {/* Debug-Informationen in der Entwicklung */}
      
      <HeroContent>
        <HeroTitle
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.2 }}
        >
          <span className="title-main">Der Ponystall</span>
          <span className="title-subtitle">Tiergestützte Pädagogik & Förderung</span>
        </HeroTitle>
        
        <HeroDescription
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.6 }}
        >
          Entdecken Sie die wunderbare Welt der tiergestützten Pädagogik mit Marcella Deeken. 
          Unsere Ponys sind mehr als nur Tiere – sie sind therapeutische Partner, die Kinder 
          bis 14 Jahren dabei helfen, zu wachsen, zu lernen und sich zu entwickeln.
        </HeroDescription>
        
        <HeroButtons
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1 }}
        >
          <HeroButton to="/kurse" className="btn-primary">
            Unsere Kurse
          </HeroButton>
          <HeroButton to="/ponys" className="btn-secondary">
            Unsere Ponys
          </HeroButton>
        </HeroButtons>
      </HeroContent>
      
      <ScrollIndicator
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.5 }}
        onClick={scrollToNext}
      >
        <div className="scroll-mouse">
          <div className="scroll-wheel"></div>
        </div>
        <span>Scrollen Sie nach unten</span>
      </ScrollIndicator>
    </HeroSection>
  );
};

export default Hero;